<template>
  <d-modal
    :active="isActive"
    :width="700"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-xl font-semibold text-secondary-500">
          {{ subjectArea.name }}
        </h3>
        <h2 class="text-4xl font-bold">
          Registered Users
        </h2>
      </div>

      <!-- Content -->
      <div class="mb-6">
        <div v-if="loading" class="flex justify-center py-10">
          <span class="loading loading-spinner loading-md"></span>
        </div>
        
        <div v-else-if="users.length === 0" class="text-center py-8">
          No registered users found for this subject area.
        </div>
        
        <div v-else class="space-y-4">
          <div 
            v-for="user in users" 
            :key="user.id" 
            class="p-4 bg-white border rounded-lg hover:bg-gray-50"
          >
            <p class="font-bold text-lg mb-2">
              {{ user.firstName }} {{ user.lastName }}
            </p>
            <div class="space-y-1 text-sm">
              <p v-if="user.email" class="flex items-center gap-2">
                <i class="fas fa-envelope text-primary-500"></i>
                {{ user.email }}
              </p>
              <p v-if="user.phoneNumber" class="flex items-center gap-2">
                <i class="fas fa-phone text-primary-500"></i>
                {{ user.phoneNumber }}
              </p>
              <p v-if="user.role" class="flex items-center gap-2">
                <i class="fas fa-user-tag text-secondary-500"></i>
                {{ user.role }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="total > pagination.pageSize" class="flex justify-center mt-6">
        <d-pagination 
          :total-items="total" 
          :per-page="pagination.pageSize" 
          v-model:current="pagination.page" 
        />
      </div>

      <!-- Modal Actions -->
      <div class="flex justify-end gap-3 pt-4 border-t">
        <d-btn 
          flat 
          @click="handleClose"
        >
          Close
        </d-btn>
      </div>
    </div>
  </d-modal>
</template>

<script>
  import axios from 'axios';
  import { toast } from '@services/toast.service';

  export default {
    name: 'SubjectAreaUserModal',

    props: {
      active: {
        type: Boolean,
        default: false,
      },
      subjectArea: {
        type: Object,
        default: () => ({}),
      },
    },

    emits: ['close'],

    data() {
      return {
        users: [],
        loading: false,
        filter: '',
        total: 0,
        pagination: {
          page: 1,
          pageSize: 10,
          sortField: 'firstName',
          sortOrder: 'asc',
        },
      };
    },

    computed: {
      isActive() {
        return this.active;
      },
    },

    watch: {
      pagination: {
        handler() {
          if (this.isActive) {
            this.getUsers();
          }
        },
        deep: true,
      },

      active: {
        handler(newVal) {
          if (newVal) {
            this.getUsers();
          }
        },
        immediate: true,
      },
    },

    methods: {
      async getUsers() {
        if (!this.subjectArea?.id) return;

        this.loading = true;
        try {
          const { page, sortField, sortOrder, pageSize } = this.pagination;
          const params = [
            `filter=${encodeURIComponent(this.filter)}`,
            `sortDir=${sortField} ${sortOrder}`,
            `currentPage=${page}`,
            `pageSize=${pageSize}`
          ].join('&');

          const { data } = await axios.get(`/api/subject-areas/${this.subjectArea.id}/users/paged?${params}`);
          this.users = data.resultItems || [];
          this.total = data.totalRecords || 0;
        } catch (error) {
          console.error('Error loading users:', error);
          toast.error('Failed to load users');
          this.users = [];
          this.total = 0;
        } finally {
          this.loading = false;
        }
      },

      handleClose() {
        this.$emit('close');
      },
    },
  };
</script>
